import { CheckCircle2, Key, UserPlus } from "lucide-react";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import apiClient from "../../api/client";
import {
	Button,
	Input,
	Modal,
	Notification,
	Select,
	Table,
	useNotification,
} from "../../components/FormComponents";
import RoleBadge from "../../components/RoleBadge";
import { ROLE_OPTIONS, ROLES } from "../../constants/roles";

const StatusBadge = ({ active }) => (
	<span
		className={`px-3 py-1 rounded-full text-xs font-semibold border inline-flex items-center gap-1 ${
			active
				? "bg-green-600/20 text-green-300 border-green-500/30"
				: "bg-red-600/20 text-red-300 border-red-500/30"
		}`}
	>
		{active ? "Ativo" : "Inativo"}
	</span>
);

StatusBadge.propTypes = {
	active: PropTypes.bool,
};

export default function Users() {
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [passwordUserId, setPasswordUserId] = useState(null);
	const [isSaving, setIsSaving] = useState(false);
	const [errors, setErrors] = useState({});
	const { notification, showNotification } = useNotification();

	const [formData, setFormData] = useState({
		email: "",
		password: "",
		name: "",
		role: "",
	});

	const [passwordData, setPasswordData] = useState({
		password: "",
		confirmPassword: "",
	});

	const fetchUsers = useCallback(async () => {
		try {
			setLoading(true);
			const response = await apiClient.get("/users");
			setUsers(response.data);
		} catch (error) {
			console.error("Error fetching users:", error);
			showNotification("Erro ao carregar usuarios", "error");
		} finally {
			setLoading(false);
		}
	}, [showNotification]);

	useEffect(() => {
		fetchUsers();
	}, [fetchUsers]);

	const validateForm = () => {
		const newErrors = {};
		if (!formData.name.trim()) newErrors.name = "Nome obrigatorio";
		if (!formData.email.trim()) newErrors.email = "Email obrigatorio";
		if (formData.email && !formData.email.includes("@"))
			newErrors.email = "Email invalido";
		if (!formData.role) newErrors.role = "Role obrigatoria";
		if (!editingId && !formData.password.trim())
			newErrors.password = "Senha obrigatoria";
		if (!editingId && formData.password.length < 6)
			newErrors.password = "Senha deve ter no minimo 6 caracteres";
		return newErrors;
	};

	const validatePassword = () => {
		const newErrors = {};
		if (!passwordData.password.trim()) newErrors.password = "Senha obrigatoria";
		if (passwordData.password.length < 6)
			newErrors.password = "Senha deve ter no minimo 6 caracteres";
		if (passwordData.password !== passwordData.confirmPassword) {
			newErrors.confirmPassword = "Senhas nao conferem";
		}
		return newErrors;
	};

	const handleOpenModal = (userData = null) => {
		setErrors({});
		if (userData) {
			setEditingId(userData.id);
			setFormData({
				email: userData.email || "",
				password: "",
				name: userData.name || "",
				role: userData.role || "",
				is_active: userData.is_active,
			});
		} else {
			setEditingId(null);
			setFormData({ email: "", password: "", name: "", role: "" });
		}
		setIsModalOpen(true);
	};

	const handleOpenPasswordModal = (userId) => {
		setErrors({});
		setPasswordUserId(userId);
		setPasswordData({ password: "", confirmPassword: "" });
		setIsPasswordModalOpen(true);
	};

	const handleSubmit = async () => {
		const formErrors = validateForm();
		if (Object.keys(formErrors).length > 0) {
			setErrors(formErrors);
			return;
		}

		try {
			setIsSaving(true);
			if (editingId) {
				const { password: _password, ...updateData } = formData;
				await apiClient.put(`/users/${editingId}`, {
					email: updateData.email,
					name: updateData.name,
					role: updateData.role,
					is_active: updateData.is_active,
				});
				showNotification("Usuario atualizado com sucesso!");
			} else {
				await apiClient.post("/users", {
					email: formData.email,
					password: formData.password,
					name: formData.name,
					role: formData.role,
				});
				showNotification("Usuario criado com sucesso!");
			}
			setIsModalOpen(false);
			fetchUsers();
		} catch (error) {
			console.error("Error saving user:", error);
			showNotification(
				error.response?.data?.error || "Erro ao salvar usuario",
				"error",
			);
		} finally {
			setIsSaving(false);
		}
	};

	const handlePasswordSubmit = async () => {
		const formErrors = validatePassword();
		if (Object.keys(formErrors).length > 0) {
			setErrors(formErrors);
			return;
		}

		try {
			setIsSaving(true);
			await apiClient.put(`/users/${passwordUserId}/password`, {
				password: passwordData.password,
			});
			showNotification("Senha redefinida com sucesso!");
			setIsPasswordModalOpen(false);
		} catch (error) {
			console.error("Error resetting password:", error);
			showNotification(
				error.response?.data?.error || "Erro ao redefinir senha",
				"error",
			);
		} finally {
			setIsSaving(false);
		}
	};

	const handleDeactivate = async (id) => {
		if (window.confirm("Tem certeza que deseja desativar este usuario?")) {
			try {
				await apiClient.delete(`/users/${id}`);
				showNotification("Usuario desativado com sucesso!");
				fetchUsers();
			} catch (error) {
				console.error("Error deactivating user:", error);
				showNotification("Erro ao desativar usuario", "error");
			}
		}
	};

	const formatLastLogin = (dateStr) => {
		if (!dateStr) return "Nunca";
		const date = new Date(dateStr);
		return date.toLocaleString("pt-BR", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const columns = [
		{ key: "id", label: "ID" },
		{
			key: "name",
			label: "Nome",
			render: (val) => <span className="font-medium text-white">{val}</span>,
		},
		{ key: "email", label: "Email" },
		{
			key: "role",
			label: "Role",
			render: (val) => <RoleBadge role={val} />,
		},
		{
			key: "is_active",
			label: "Status",
			render: (val) => <StatusBadge active={val} />,
		},
		{
			key: "last_login",
			label: "Ultimo Login",
			render: (val) => (
				<span className="text-slate-400 text-xs">{formatLastLogin(val)}</span>
			),
		},
	];

	return (
		<div className="space-y-6">
			<Notification notification={notification} />

			{/* Header */}
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-white">Gerenciar Usuarios</h1>
				<Button onClick={() => handleOpenModal()}>
					<UserPlus className="w-4 h-4 inline mr-2" />
					Novo Usuario
				</Button>
			</div>

			{/* Table */}
			<div className="bg-slate-800/50 backdrop-blur border border-white/5 rounded-xl p-6">
				<Table
					columns={columns}
					data={users}
					loading={loading}
					onEdit={handleOpenModal}
					onDelete={handleDeactivate}
					actions={(row) => (
						<button
							type="button"
							onClick={() => handleOpenPasswordModal(row.id)}
							className="px-3 py-1 bg-amber-600/20 hover:bg-amber-600/30 text-amber-300 rounded text-xs transition-all active:scale-95 flex items-center gap-1"
						>
							<Key className="w-3 h-3" /> Redefinir Senha
						</button>
					)}
				/>
			</div>

			{/* Create / Edit User Modal */}
			<Modal
				isOpen={isModalOpen}
				title={editingId ? "Editar Usuario" : "Novo Usuario"}
				onClose={() => {
					setIsModalOpen(false);
					setErrors({});
				}}
				onSubmit={handleSubmit}
				submitLabel={editingId ? "Atualizar" : "Criar"}
				loading={isSaving}
			>
				<div className="space-y-4">
					<Input
						label="Nome *"
						value={formData.name}
						onChange={(e) => {
							setFormData({ ...formData, name: e.target.value });
							if (errors.name) setErrors({ ...errors, name: null });
						}}
						placeholder="Nome completo"
						error={errors.name}
					/>
					<Input
						label="Email *"
						type="email"
						value={formData.email}
						onChange={(e) => {
							setFormData({ ...formData, email: e.target.value });
							if (errors.email) setErrors({ ...errors, email: null });
						}}
						placeholder="usuario@empresa.com"
						error={errors.email}
					/>
					{!editingId && (
						<Input
							label="Senha *"
							type="password"
							value={formData.password}
							onChange={(e) => {
								setFormData({ ...formData, password: e.target.value });
								if (errors.password) setErrors({ ...errors, password: null });
							}}
							placeholder="Minimo 6 caracteres"
							error={errors.password}
						/>
					)}
					<Select
						label="Role *"
						value={formData.role}
						onChange={(e) => {
							setFormData({ ...formData, role: e.target.value });
							if (errors.role) setErrors({ ...errors, role: null });
						}}
						options={ROLE_OPTIONS}
						placeholder="Selecione uma role"
						error={errors.role}
					/>
					{formData.role && ROLES[formData.role] && (
						<div className="p-3 bg-slate-800/50 border border-white/5 rounded-lg">
							<div className="flex items-center gap-2 mb-2">
								<RoleBadge role={formData.role} size="xs" />
								<span className="text-xs text-slate-400">pode:</span>
							</div>
							<ul className="space-y-1">
								{ROLES[formData.role].permissions.map((perm, i) => (
									<li
										key={`perm-${i}`}
										className="flex items-center gap-2 text-xs text-slate-300"
									>
										<CheckCircle2 className="w-3 h-3 text-green-400 flex-shrink-0" />
										{perm}
									</li>
								))}
							</ul>
						</div>
					)}
					{editingId && (
						<div className="mb-4">
							<span className="block text-sm font-medium text-slate-300 mb-2">
								Status
							</span>
							<div className="flex items-center gap-3">
								<button
									id="user-status-toggle"
									type="button"
									onClick={() =>
										setFormData({ ...formData, is_active: !formData.is_active })
									}
									className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
										formData.is_active ? "bg-cyan-600" : "bg-slate-700"
									}`}
									aria-pressed={formData.is_active}
								>
									<span
										className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
											formData.is_active ? "translate-x-6" : "translate-x-1"
										}`}
									/>
								</button>
								<label
									htmlFor="user-status-toggle"
									className="text-sm text-slate-300 cursor-pointer"
								>
									{formData.is_active ? "Ativo" : "Inativo"}
								</label>
							</div>
						</div>
					)}
				</div>
			</Modal>

			{/* Reset Password Modal */}
			<Modal
				isOpen={isPasswordModalOpen}
				title="Redefinir Senha"
				onClose={() => {
					setIsPasswordModalOpen(false);
					setErrors({});
				}}
				onSubmit={handlePasswordSubmit}
				submitLabel="Redefinir Senha"
				loading={isSaving}
			>
				<div className="space-y-4">
					<div className="p-3 bg-amber-600/10 border border-amber-500/20 rounded-lg text-xs text-amber-300 flex items-center gap-2">
						<Key className="w-4 h-4 flex-shrink-0" />A nova senha sera aplicada
						imediatamente. O usuario precisara usar a nova senha no proximo
						login.
					</div>
					<Input
						label="Nova Senha *"
						type="password"
						value={passwordData.password}
						onChange={(e) => {
							setPasswordData({ ...passwordData, password: e.target.value });
							if (errors.password) setErrors({ ...errors, password: null });
						}}
						placeholder="Minimo 6 caracteres"
						error={errors.password}
					/>
					<Input
						label="Confirmar Senha *"
						type="password"
						value={passwordData.confirmPassword}
						onChange={(e) => {
							setPasswordData({
								...passwordData,
								confirmPassword: e.target.value,
							});
							if (errors.confirmPassword)
								setErrors({ ...errors, confirmPassword: null });
						}}
						placeholder="Repita a senha"
						error={errors.confirmPassword}
					/>
				</div>
			</Modal>
		</div>
	);
}
